import { useLayoutEffect, useRef, useState } from 'react';
import { IconSearch } from './icons';
import { Link } from 'react-router-dom';
import type { BetaSearch } from '../repositories/betaSearchRepository';
import { useReaderRoutes } from '../providers/ReaderRoutesProvider';

export function fittingChips(widths: number[], available: number, viewWidth: number, overflowWidth: number, gap = 8): number {
  for (let count = Math.min(4, widths.length); count >= 0; count--) {
    const overflow = count < widths.length;
    const needed = widths.slice(0, count).reduce((sum, width) => sum + width, 0) + viewWidth +
      (overflow ? overflowWidth : 0) + gap * (count + (overflow ? 1 : 0));
    if (needed <= available) return count;
  }
  return 0;
}
export function ReaderFeedChips({ interests, busy, onToggle }: {
  interests: BetaSearch[]; busy: boolean; onToggle: (feed: BetaSearch) => void;
}) {
  const routes = useReaderRoutes();
  const selected = interests.filter(feed => feed.included);
  const [count, setCount] = useState(Math.min(4, selected.length));
  const root = useRef<HTMLDivElement>(null);
  const measure = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const resize = () => {
      if (!root.current || !measure.current) return;
      const children = Array.from(measure.current.children) as HTMLElement[];
      const widths = children.slice(0, selected.length).map(child => child.getBoundingClientRect().width);
      setCount(fittingChips(widths, root.current.getBoundingClientRect().width,
        children[selected.length].getBoundingClientRect().width, children[selected.length + 1].getBoundingClientRect().width));
    };
    resize(); const observer = new ResizeObserver(resize); observer.observe(root.current!);
    void document.fonts?.ready.then(resize);
    return () => observer.disconnect();
  }, [interests]);
  const chip = (feed: BetaSearch, measurement = false) => <div className="beta-pill owned selected" key={feed.id}>
    <Link to={routes.feed(feed.id)} tabIndex={measurement ? -1 : undefined} className="beta-pill-toggle" title={feed.name}>
      {!feed.is_public && <IconSearch size={12} />}<span className="reader-chip-name">{feed.name}</span>
    </Link>
    <button tabIndex={measurement ? -1 : undefined} type="button" className="beta-pill-action" aria-label={`Remove ${feed.name} from Home`} title={`Remove ${feed.name} from Home`} disabled={busy} onClick={() => onToggle(feed)}>×</button>
  </div>;
  const view = <Link className="reader-view-feeds" to={routes.feeds}><svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" aria-hidden="true"><path d="M4 4h10M4 8h10M4 12h10M1 4h1M1 8h1M1 12h1" /></svg>View feeds</Link>;
  const shown = Math.min(count, selected.length);
  return <div className="reader-chip-container" ref={root}>
    <div className="beta-interest-pills" aria-label="Feeds shown in Home">{selected.slice(0, shown).map(feed => chip(feed))}
      {shown < selected.length && <Link to={routes.feeds} className="reader-chip-overflow" title={`Also in Home: ${selected.slice(shown).map(feed => feed.name).join(', ')}`} aria-label={`${selected.length - shown} more feeds in Home. View feeds`}>+{selected.length - shown}</Link>}{view}</div>
    <div className="reader-chip-measure" ref={measure} aria-hidden="true" inert>{selected.map(feed => chip(feed, true))}{view}<span className="reader-chip-overflow">+{selected.length}</span></div>
  </div>;
}
